<template>
  <ValidationObserver ref="observer" v-slot="{ handleSubmit }">
    <p class="mb-3">{{ $t("registerPage.selection-profil-indications") }}</p>
    <div class="selectionProfil">
      <RadioWithInputAndValidation
        v-model="selectedProfil"
        rules="required"
        :data="profils"
        :option-labels="profilsLabels"
        :vertical-display="true"
        :help-notices="profilsHelpNotices"
      />
      <RadioWithInputAndValidation
        v-if="isSousProfilSelected"
        v-model="selectedSousProfil"
        class="radioSubGroup"
        rules="required"
        :data="sousProfils"
        :option-labels="sousProfilsLabels"
        :vertical-display="true"
        :help-notices="sousProfilsHelpNotices"
      />
    </div>
    <div class="buttons mt-5">
      <b-button type="is-dark" @click="goBack">
        {{ $t("registerPage.precedent") }}
      </b-button>
      <b-button type="is-primary" @click="handleSubmit(submit)">
        {{ $t("registerPage.suivant") }}
      </b-button>
    </div>
  </ValidationObserver>
</template>

<script lang="ts">
import type { ProfilUtilisateur } from "@/model/bean/GeneratedDTOs"
import { defineComponent, PropType } from "vue"
import ValidationObserver from "@/services/validation/ValidationObserver.vue"
import RadioWithInputAndValidation from "@/views/inputs/RadioWithInputAndValidation.vue"
import { BButton } from "buefy"

const PROFILS_DATA = ["ENSEIGNANT", "REFERENT", "SOUS_PROFIL"] as const
const SOUS_PROFILS_DATA = [
  "ACCOMPAGNATEUR_REGIONAL",
  "OBSERVATEUR_NATIONAL",
  "ACCOMPAGNATEUR_NATIONAL",
] as const

export default defineComponent({
  name: "ProfilSelectionView",
  components: {
    ValidationObserver,
    RadioWithInputAndValidation,
    BButton,
  },
  props: {
    modelValue: {
      type: String as PropType<ProfilUtilisateur>,
      required: false,
      default: undefined,
    },
  },

  emits: ["go-back", "update:modelValue"],
  data() {
    return {
      profils: ["ENSEIGNANT", "REFERENT", "SOUS_PROFIL"] as string[],
      sousProfils: [
        "ACCOMPAGNATEUR_REGIONAL",
        "OBSERVATEUR_NATIONAL",
        "ACCOMPAGNATEUR_NATIONAL",
      ] as ProfilUtilisateur[],
      selectedProfil: "ENSEIGNANT" as string,
      selectedSousProfil: "ACCOMPAGNATEUR_REGIONAL" as ProfilUtilisateur,
      isSousProfilSelected: false,
    }
  },
  computed: {
    profilsLabels(): string[] {
      return PROFILS_DATA.map((profil: string) => {
        return this.$t(
          `registerPage.profil-label-${profil.toLowerCase().replace(/_/g, "-")}`,
        ).toString()
      })
    },
    profilsHelpNotices(): string[] {
      return PROFILS_DATA.map((profil: string) => {
        return this.$t(`registerPage.tooltip-${profil.toLowerCase().replace(/_/g, "-")}`).toString()
      })
    },
    sousProfilsLabels(): string[] {
      return SOUS_PROFILS_DATA.map((profil: ProfilUtilisateur) => {
        return this.$t(
          `registerPage.profil-label-${profil.toLowerCase().replace(/_/g, "-")}`,
        ).toString()
      })
    },
    sousProfilsHelpNotices(): string[] {
      return SOUS_PROFILS_DATA.map((profil: ProfilUtilisateur) => {
        return this.$t(`registerPage.tooltip-${profil.toLowerCase().replace(/_/g, "-")}`).toString()
      })
    },
  },
  watch: {
    selectedProfil(): void {
      this.isSousProfilSelected = this.selectedProfil === "SOUS_PROFIL"
    },
  },
  created(): void {
    this.selectedProfil = this.profils[0]
    this.selectedSousProfil = this.sousProfils[0]

    if (this.modelValue) {
      if (this.modelValue === "ENSEIGNANT" || this.modelValue === "REFERENT") {
        this.selectedProfil = this.modelValue
      } else {
        this.selectedProfil = "SOUS_PROFIL"
        this.selectedSousProfil = this.modelValue
      }
    }
  },
  methods: {
    getInputSelectProfil(): ProfilUtilisateur {
      if (this.isSousProfilSelected) {
        return this.selectedSousProfil
      } else {
        // Convertir selectedProfil en ProfilUtilisateur si valide
        if (this.isValidProfil(this.selectedProfil)) {
          return this.selectedProfil as ProfilUtilisateur
        }
        throw new Error("Le profil sélectionné est invalide !")
      }
    },

    isValidProfil(profil: string): profil is ProfilUtilisateur {
      return [
        "SUPER_ADMINISTRATEUR",
        "ACCOMPAGNATEUR_NATIONAL",
        "ACCOMPAGNATEUR_REGIONAL",
        "ANIMATEUR_REGIONAL",
        "REFERENT",
        "ENSEIGNANT",
        "OBSERVATEUR_NATIONAL",
      ].includes(profil)
    },

    submit(): void {
      const profil = this.getInputSelectProfil()
      this.$emit("update:modelValue", profil)
    },

    goBack(): void {
      const profil = this.getInputSelectProfil()
      this.$emit("go-back", profil)
    },
  },
})
</script>
<style scoped lang="scss">
.buttons {
  justify-content: space-between;
}
</style>

<style lang="scss">
.selectionProfil .RadioWithInputAndValidation-field {
  margin-bottom: 0;
  padding-top: 0;

  &.radioSubGroup {
    padding-left: 5rem;
  }
}
</style>
